"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.safeDateExtractFromJson = exports.areJsonEqual = exports.sanitizeJson = exports.sortJsonByKeys = exports.toJson = exports.getStaticCopy = void 0;
const string_utils_1 = require("./string.utils");
// eslint-disable-next-line @typescript-eslint/no-restricted-types
const executeUpThePrototypeChain = (obj, func) => {
    if (!(obj instanceof Object)) {
        return [];
    }
    const results = [];
    let prototype = obj;
    do {
        results.push(func(prototype));
        prototype = Object.getPrototypeOf(prototype);
    } while (prototype);
    return results;
};
const getEvaluatedGetters = (obj) => {
    if (!(obj instanceof Object)) {
        return {};
    }
    const getters = Object.entries(Object.getOwnPropertyDescriptors(obj)).reduce((acc, [name, descriptor]) => {
        if (typeof descriptor.get === 'function') {
            acc.push([name, descriptor.get]);
        }
        return acc;
    }, []);
    return getters.reduce((acc, [name, getter]) => {
        try {
            acc[name] = getter.call(obj);
        }
        catch (err) {
            const message = 'message' in err ? err.message : 'Error when calling getter';
            acc[name] = message;
        }
        return acc;
    }, {});
};
const getAllEvaluatedGetters = (obj) => {
    if (!(obj instanceof Object)) {
        return {};
    }
    const getters = executeUpThePrototypeChain(obj, (prototype) => getEvaluatedGetters(prototype));
    return getters.reduce((acc, getter) => ({ ...acc, ...getter }), {});
};
/**
 * Returns an object containing all the fields of the given object even if they are not enumerable, excluding getters or methods.
 * One exception is Dates, which are objects with only methods in JS. Dates will be converted to ISO strings.
 */
const getOwnFields = (obj) => {
    if (!(obj instanceof Object)) {
        return {};
    }
    return Object.entries(Object.getOwnPropertyDescriptors(obj)).reduce((acc, [name, descriptor]) => {
        if (typeof descriptor.value !== 'undefined' && typeof descriptor.value !== 'function') {
            // we need to handle Dates separately because they are objects with only methods in JS. The data for their date
            // representation is directly accessible.
            const value = descriptor.value instanceof Date ? descriptor.value.toISOString() : descriptor.value;
            acc[name] = value;
        }
        return acc;
    }, {});
};
const getAllFields = (obj) => {
    if (!(obj instanceof Object)) {
        return {};
    }
    const fields = executeUpThePrototypeChain(obj, (prototype) => getOwnFields(prototype));
    return fields.reduce((acc, field) => ({ ...acc, ...field }), {});
};
/**
 * Constructs a static copy of an object, including all getters which have been evaluated and added to the copy with the
 * same key as the getter in the original object. This function is recursive, so all nested objects will also be copied.
 *
 * Circular references will be replaced with the string '[Circular <ClassName>]' in the copy.
 * Symbol values will be replaced with the string '[Symbol(<SymbolName>)]' in the copy.
 *
 * @param obj - The object to copy
 * @param fieldsToCopy - An optional list of fields to copy if {@link obj} is an Object. If this is not provided, all fields will be copied.
 * @param seenBranches - A set of objects that have already been copied. This is used to detect circular references.
 * @returns - A deep copy of the object, including all evaluated getters.
 */
const getStaticCopy = (obj, fieldsToCopy = null, 
// eslint-disable-next-line @typescript-eslint/no-restricted-types
seenBranches = new Set()) => {
    // primitive values are pass by value, so we can just return them
    if (!(obj instanceof Object)) {
        // feel free to improve the typing here to prevent having to do an unchecked cast
        return obj;
    }
    seenBranches.add(obj);
    const getters = getAllEvaluatedGetters(obj);
    const fields = getAllFields(obj);
    let entries = Object.entries({ ...fields, ...getters });
    if (fieldsToCopy) {
        // create a set from the array to make the lookup faster
        const fieldsToCopySet = new Set(fieldsToCopy);
        entries = entries.filter(([key]) => fieldsToCopySet.has(key));
    }
    const newFields = entries.map(([key, value]) => {
        if (value instanceof Object) {
            if (seenBranches.has(value)) {
                const constructorName = value.constructor.name;
                return [key, `[Circular ${constructorName}]`];
            }
            seenBranches.add(value);
            return [key, (0, exports.getStaticCopy)(value, null, seenBranches)];
        }
        if (typeof value === 'symbol') {
            return [key, `[Symbol(${value.description})]`];
        }
        return [key, value];
    });
    return Object.fromEntries(newFields);
};
exports.getStaticCopy = getStaticCopy;
/**
 * Convert just about anything to JSON. The following limitations prevent the generated JSON from being used to reconstruct the
 * original object:
 * - objects will be ignored
 * - circular references will be replaced with the string '[Circular <ClassName>]'
 * - Symbol values will be replaced with the string '[Symbol(<SymbolName>)]' in the copy
 *
 * @param value - The value to convert to JSON
 * @param replacer — An array of strings and numbers that acts as an approved list for selecting the object properties that will be stringified.
 * Defaults to null, which means all properties will be included.
 * @param space — Adds indentation, white space, and line break characters to the return-value JSON text to make it easier to read.
 * Defaults to 2.
 * @returns - The JSON representation of the value, including all evaluated getters and fields up the prototype chain.
 */
const toJson = (value, replacer = null, space = 2) => JSON.stringify((0, exports.getStaticCopy)(value, replacer), null, space);
exports.toJson = toJson;
const sortJsonByKeys = (json) => {
    if (Array.isArray(json)) {
        // Sort arrays recursively
        return json.map(exports.sortJsonByKeys);
    }
    else if (json !== null && typeof json === 'object') {
        // Sort object keys
        return Object.keys(json)
            .sort()
            .reduce((acc, key) => {
            acc[key] = (0, exports.sortJsonByKeys)(json[key]);
            return acc;
        }, {});
    }
    // return primitives as the raw value
    return json;
};
exports.sortJsonByKeys = sortJsonByKeys;
/**
 * Recursively sanitizes all strings in a JSON structure by removing control characters.
 * This prevents postgres errors when saving text/jsonb containing null bytes or other control chars.
 */
const sanitizeJson = (json) => {
    if (Array.isArray(json)) {
        return json.map(exports.sanitizeJson);
    }
    else if (json !== null && typeof json === 'object') {
        return Object.keys(json).reduce((acc, key) => {
            acc[(0, string_utils_1.sanitizeString)(key)] = (0, exports.sanitizeJson)(json[key]);
            return acc;
        }, {});
    }
    else if (typeof json === 'string') {
        return (0, string_utils_1.sanitizeString)(json);
    }
    return json;
};
exports.sanitizeJson = sanitizeJson;
const areJsonEqual = (json1, json2) => JSON.stringify((0, exports.sortJsonByKeys)(json1)) === JSON.stringify((0, exports.sortJsonByKeys)(json2));
exports.areJsonEqual = areJsonEqual;
/**
 * Safely extracts a nested property from a Json object and converts it to the specified type
 * @param obj - The Json object to extract from
 * @param path - Array of property names to traverse
 * @returns The converted value or null if the path doesn't exist or validation fails
 */
const safeDateExtractFromJson = (obj, path) => {
    if (!obj || typeof obj !== 'object' || obj === null) {
        return null;
    }
    let current = obj;
    for (const key of path) {
        if (typeof current !== 'object' || current === null || !(key in current)) {
            return null;
        }
        current = current[key];
    }
    if (typeof current !== 'string' && typeof current !== 'number') {
        return null;
    }
    if (typeof current === 'string' && current.trim() === '') {
        return null;
    }
    const date = new Date(current);
    if (isNaN(date.getTime())) {
        return null;
    }
    return date;
};
exports.safeDateExtractFromJson = safeDateExtractFromJson;
